import { values } from 'lodash';
import { createSelector } from 'reselect';
import { denormalize } from 'normalizr';
import {
  changeImpact as changeImpactSchema,
  changeImpactParticipants as changeImpactParticipantsSchema,
  changeImpactParticipants,
  impactedEntities as impactedEntitiesSchema,
} from '../lib/schema.js';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { selectEntities } from './entities.js';

// API Actions

const RETRIEVE_CHANGE_IMPACTS_REQUEST =
  'changesync/projects/RETRIEVE_CHANGE_IMPACTS_REQUEST';
const RETRIEVE_CHANGE_IMPACTS_SUCCESS =
  'changesync/projects/RETRIEVE_CHANGE_IMPACTS_SUCCESS';
const RETRIEVE_CHANGE_IMPACTS_FAILURE =
  'changesync/projects/RETRIEVE_CHANGE_IMPACTS_FAILURE';
const RETRIEVE_CHANGE_IMPACT_REQUEST =
  'changesync/projects/RETRIEVE_CHANGE_IMPACT_REQUEST';
const RETRIEVE_CHANGE_IMPACT_SUCCESS =
  'changesync/projects/RETRIEVE_CHANGE_IMPACT_SUCCESS';
const RETRIEVE_CHANGE_IMPACT_FAILURE =
  'changesync/projects/RETRIEVE_CHANGE_IMPACT_FAILURE';
const SEARCH_CHANGE_IMPACT_PARTICIPANTS_REQUEST =
  'changesync/projects/SEARCH_CHANGE_IMPACT_PARTICIPANTS_REQUEST';
const SEARCH_CHANGE_IMPACT_PARTICIPANTS_SUCCESS =
  'changesync/projects/SEARCH_CHANGE_IMPACT_PARTICIPANTS_SUCCESS';
const SEARCH_CHANGE_IMPACT_PARTICIPANTS_FAILURE =
  'changesync/projects/SEARCH_CHANGE_IMPACT_PARTICIPANTS_FAILURE';
const RETRIEVE_IMPACTED_ENTITIES_REQUEST =
  'changesync/projects/RETRIEVE_IMPACTED_ENTITIES_REQUEST';
const RETRIEVE_IMPACTED_ENTITIES_SUCCESS =
  'changesync/projects/RETRIEVE_IMPACTED_ENTITIES_SUCCESS';
const RETRIEVE_IMPACTED_ENTITIES_FAILURE =
  'changesync/projects/RETRIEVE_IMPACTED_ENTITIES_FAILURE';
const CREATE_CHANGE_IMPACT_REQUEST =
  'changesync/projects/CREATE_CHANGE_IMPACT_REQUEST';
const CREATE_CHANGE_IMPACT_SUCCESS =
  'changesync/projects/CREATE_CHANGE_IMPACT_SUCCESS';
const CREATE_CHANGE_IMPACT_FAILURE =
  'changesync/projects/CREATE_CHANGE_IMPACT_FAILURE';
const DELETE_CHANGE_IMPACT_REQUEST =
  'changesync/projects/DELETE_CHANGE_IMPACT_REQUEST';
const DELETE_CHANGE_IMPACT_SUCCESS =
  'changesync/projects/DELETE_CHANGE_IMPACT_SUCCESS';
const DELETE_CHANGE_IMPACT_FAILURE =
  'changesync/projects/DELETE_CHANGE_IMPACT_FAILURE';
const UPDATE_CHANGE_IMPACT_REQUEST =
  'changesync/projects/UPDATE_CHANGE_IMPACT_REQUEST';
const UPDATE_CHANGE_IMPACT_SUCCESS =
  'changesync/projects/UPDATE_CHANGE_IMPACT_SUCCESS';
const UPDATE_CHANGE_IMPACT_FAILURE =
  'changesync/projects/UPDATE_CHANGE_IMPACT_FAILURE';
const SEARCH_CHANGE_IMPACTS_REQUEST =
  'changesync/projects/SEARCH_CHANGE_IMPACTS_REQUEST';
const SEARCH_CHANGE_IMPACTS_SUCCESS =
  'changesync/projects/SEARCH_CHANGE_IMPACTS_SUCCESS';
const SEARCH_CHANGE_IMPACTS_FAILURE =
  'changesync/projects/SEARCH_CHANGE_IMPACTS_FAILURE';
const IMPORT_CHANGE_IMPACT_DATA_REQUEST =
  'changesync/projects/IMPORT_CHANGE_IMPACT_DATA_REQUEST';
const IMPORT_CHANGE_IMPACT_DATA_SUCCESS =
  'changesync/projects/IMPORT_CHANGE_IMPACT_DATA_SUCCESS';
const IMPORT_CHANGE_IMPACT_DATA_FAILURE =
  'changesync/projects/IMPORT_CHANGE_IMPACT_DATA_FAILURE';
const HIDE_DATA_IMPORT_ERROR_REQUEST =
  'changesync/projects/HIDE_DATA_IMPORT_ERROR_REQUEST';
const HIDE_DATA_IMPORT_ERROR_SUCCESS =
  'changesync/projects/HIDE_DATA_IMPORT_ERROR_SUCCESS';
const HIDE_DATA_IMPORT_ERROR_FAILURE =
  'changesync/projects/IMPORT_CHANGE_IMPACT_DATA_FAILURE';
const UPDATE_SEARCH_QUERY = 'changesync/projects/UPDATE_SEARCH_QUERY';
const SEARCH_CHANGE_IMPACT = 'changesync/projects/SEARCH_CHANGE_IMPACT';
const SEARCH_PARTICIPANTS = 'changesync/projects/SEARCH_PARTICIPANTS';

const initialState = {
  retrieveChangeImpacts: {
    pending: false,
    success: false,
    error: null,
  },

  retrieveChangeImpact: {
    pending: false,
    success: false,
    error: null,
  },

  retrieveImpactedEntities: {
    pending: false,
    success: false,
    error: null,
  },

  searchChangeImpactParticipants: {
    pending: false,
    success: false,
    error: null,
  },

  searchChangeImpacts: {
    changeImpactIds: [],
    query: {},
    pending: false,
    success: false,
    error: null,
  },
};

// Reducer

export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_CHANGE_IMPACT_REQUEST:
      return {
        ...state,
        retrieveChangeImpact: {
          ...state.retrieveChangeImpact,
          pending: true,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_CHANGE_IMPACT_SUCCESS:
      return {
        ...state,
        retrieveChangeImpact: {
          ...state.retrieveChangeImpact,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_CHANGE_IMPACT_FAILURE:
      return {
        ...state,
        retrieveChangeImpact: {
          ...state.retrieveChangeImpact,
          pending: false,
          error: action.error,
        },
      };
    case RETRIEVE_CHANGE_IMPACTS_REQUEST:
      return {
        ...state,
        retrieveChangeImpacts: {
          ...state.retrieveChangeImpacts,
          pending: true,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_CHANGE_IMPACTS_SUCCESS:
      return {
        ...state,
        retrieveChangeImpacts: {
          ...state.retrieveChangeImpacts,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_CHANGE_IMPACTS_FAILURE:
      return {
        ...state,
        retrieveChangeImpacts: {
          ...state.retrieveChangeImpacts,
          pending: false,
          error: action.error,
        },
      };
    case RETRIEVE_IMPACTED_ENTITIES_REQUEST:
      return {
        ...state,
        retrieveImpactedEntities: {
          ...state.retrieveImpactedEntities,
          pending: true,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_IMPACTED_ENTITIES_SUCCESS:
      return {
        ...state,
        retrieveImpactedEntities: {
          ...state.retrieveImpactedEntities,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_IMPACTED_ENTITIES_FAILURE:
      return {
        ...state,
        searchChangeImpactParticipants: {
          ...state.searchChangeImpactParticipants,
          pending: false,
          error: action.error,
        },
      };
    case SEARCH_CHANGE_IMPACT_PARTICIPANTS_REQUEST:
      return {
        ...state,
        searchChangeImpactParticipants: {
          ...state.searchChangeImpactParticipants,
          pending: true,
          success: false,
          error: null,
        },
      };
    case SEARCH_CHANGE_IMPACT_PARTICIPANTS_SUCCESS:
      return {
        ...state,
        searchChangeImpactParticipants: {
          ...state.searchChangeImpactParticipants,
          pending: false,
          success: true,
        },
      };
    case SEARCH_CHANGE_IMPACT_PARTICIPANTS_FAILURE:
      return {
        ...state,
        retrieveImpactedEntities: {
          ...state.retrieveImpactedEntities,
          pending: false,
          error: action.error,
        },
      };
    case SEARCH_CHANGE_IMPACTS_REQUEST:
      return {
        ...state,
        searchChangeImpacts: {
          ...state.searchChangeImpacts,
          pending: true,
          success: false,
          error: null,
        },
      };
    case SEARCH_CHANGE_IMPACTS_SUCCESS:
      return {
        ...state,
        searchChangeImpacts: {
          ...state.searchChangeImpacts,
          changeImpactIds: action.payload.result,
          query: action.meta.query,
          pending: false,
          success: true,
        },
      };
    case SEARCH_CHANGE_IMPACTS_FAILURE:
      return {
        ...state,
        searchChangeImpacts: {
          ...state.searchChangeImpacts,
          pending: false,
          error: action.error,
        },
      };
    case UPDATE_SEARCH_QUERY:
      return {
        ...state,
        query: action.payload,
      };
    default:
      return state;
  }
};

export const retrieveChangeImpacts =
  (projectId, disabledCamelizeKeys = false) =>
  (dispatch) =>
    dispatch({
      [CALL_API]: {
        types: [
          RETRIEVE_CHANGE_IMPACTS_REQUEST,
          RETRIEVE_CHANGE_IMPACTS_SUCCESS,
          RETRIEVE_CHANGE_IMPACTS_FAILURE,
        ],
        endpoint: `projects/${projectId}/change_impacts`,
        method: HTTP_METHODS.GET,
        schema: [changeImpactSchema],
        disabledCamelizeKeys,
      },
    });

export const retrieveChangeImpact =
  (changeImpactId, disabledCamelizeKeys = false) =>
  (dispatch) =>
    dispatch({
      [CALL_API]: {
        types: [
          RETRIEVE_CHANGE_IMPACT_REQUEST,
          RETRIEVE_CHANGE_IMPACT_SUCCESS,
          RETRIEVE_CHANGE_IMPACT_FAILURE,
        ],
        endpoint: `change_impacts/${changeImpactId}`,
        method: HTTP_METHODS.GET,
        schema: changeImpactSchema,
        disabledCamelizeKeys,
      },
    });

export const createChangeImpact = (projectId, changeImpact) => (dispatch) =>
  dispatch({
    [CALL_API]: {
      types: [
        CREATE_CHANGE_IMPACT_REQUEST,
        CREATE_CHANGE_IMPACT_SUCCESS,
        CREATE_CHANGE_IMPACT_FAILURE,
      ],
      endpoint: `projects/${projectId}/change_impacts`,
      method: HTTP_METHODS.POST,
      schema: changeImpactSchema,
      body: changeImpact,
      disabledCamelizeKeys: true,
    },
  });

export const updateChangeImpact =
  (changeImpact, changeImpactId) => (dispatch) =>
    dispatch({
      [CALL_API]: {
        types: [
          UPDATE_CHANGE_IMPACT_REQUEST,
          UPDATE_CHANGE_IMPACT_SUCCESS,
          UPDATE_CHANGE_IMPACT_FAILURE,
        ],
        endpoint: `change_impacts/${changeImpactId}`,
        method: HTTP_METHODS.PUT,
        schema: changeImpactSchema,
        body: changeImpact,
        disabledCamelizeKeys: true,
      },
    });

export const deleteChangeImpact = (changeImpactId) => (dispatch) =>
  dispatch({
    [CALL_API]: {
      types: [
        DELETE_CHANGE_IMPACT_REQUEST,
        DELETE_CHANGE_IMPACT_SUCCESS,
        DELETE_CHANGE_IMPACT_FAILURE,
      ],
      endpoint: `change_impacts/${changeImpactId}`,
      method: HTTP_METHODS.DELETE,
    },
  });

export const searchChangeImpacts = (projectId, query) => (dispatch) => {
  // search change impacts related to current selected project

  const { status, ...params } = query;

  return dispatch({
    [CALL_API]: {
      types: [
        SEARCH_CHANGE_IMPACTS_REQUEST,
        SEARCH_CHANGE_IMPACTS_SUCCESS,
        SEARCH_CHANGE_IMPACTS_FAILURE,
      ],
      endpoint: `projects/${projectId}/change_impacts/search`,
      method: HTTP_METHODS.GET,
      query: {
        ...params,
      },
      schema: [changeImpactSchema],
    },
    meta: {
      query,
    },
  });
};

export const searchChangeImpactParticipants =
  (changeImpactId, query) => (dispatch) => {
    const searchParam = `participations?search=${query || ''}`;

    return dispatch({
      [CALL_API]: {
        types: [
          SEARCH_CHANGE_IMPACT_PARTICIPANTS_REQUEST,
          SEARCH_CHANGE_IMPACT_PARTICIPANTS_SUCCESS,
          SEARCH_CHANGE_IMPACT_PARTICIPANTS_FAILURE,
        ],
        endpoint: `change_impacts/${changeImpactId}/${searchParam}`,
        method: HTTP_METHODS.GET,
        schema: changeImpactParticipants,
      },
      meta: {
        debounce: {
          time: 400,
          key: SEARCH_PARTICIPANTS,
        },
      },
    });
  };
export const retrieveImpactedEntities = (projectId) => (dispatch) =>
  dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_IMPACTED_ENTITIES_REQUEST,
        RETRIEVE_IMPACTED_ENTITIES_SUCCESS,
        RETRIEVE_IMPACTED_ENTITIES_FAILURE,
      ],
      endpoint: `projects/${projectId}/change_impacts/load_relations`,
      method: HTTP_METHODS.GET,
      schema: impactedEntitiesSchema,
    },
  });

export const searchOrganizationChangeImpacts =
  (organizationId, query) => (dispatch) => {
    // search for change impacts in all projects

    const { status, ...params } = query;

    return dispatch({
      [CALL_API]: {
        types: [
          SEARCH_CHANGE_IMPACTS_REQUEST,
          SEARCH_CHANGE_IMPACTS_SUCCESS,
          SEARCH_CHANGE_IMPACTS_FAILURE,
        ],
        endpoint: `organizations/${organizationId}/change_impacts/search`,
        method: HTTP_METHODS.GET,
        query: {
          ...params,
        },
        schema: [changeImpactSchema],
      },
      meta: {
        query,
      },
    });
  };

export const retrieveOrganizationImpactedEntities =
  (organizationId) => (dispatch) =>
    dispatch({
      [CALL_API]: {
        types: [
          RETRIEVE_IMPACTED_ENTITIES_REQUEST,
          RETRIEVE_IMPACTED_ENTITIES_SUCCESS,
          RETRIEVE_IMPACTED_ENTITIES_FAILURE,
        ],
        endpoint: `organizations/${organizationId}/change_impacts/load_relations`,
        method: HTTP_METHODS.GET,
        schema: impactedEntitiesSchema,
      },
    });

export const importChangeImpactData = (projectId, formData) => ({
  [CALL_API]: {
    types: [
      IMPORT_CHANGE_IMPACT_DATA_REQUEST,
      IMPORT_CHANGE_IMPACT_DATA_SUCCESS,
      IMPORT_CHANGE_IMPACT_DATA_FAILURE,
    ],
    endpoint: `projects/${projectId}/change_impacts/bulk_upload`,
    method: HTTP_METHODS.POST,
    body: formData,
  },
});

export const hideCIDataImportError = (projectId) => ({
  [CALL_API]: {
    method: HTTP_METHODS.PUT,
    types: [
      HIDE_DATA_IMPORT_ERROR_REQUEST,
      HIDE_DATA_IMPORT_ERROR_SUCCESS,
      HIDE_DATA_IMPORT_ERROR_FAILURE,
    ],
    endpoint: `projects/${projectId}/hide_impacts_errors`,
  },
});

// Selectors

export const selectChangeImpacts = (state) =>
  values(state.entities.changeImpacts);

export const selectRetrieveChangeImpactsState = (state) =>
  state.changeImpacts.retrieveChangeImpacts;

export const selectRetrieveChangeImpactState = createSelector(
  (state) => state.changeImpacts,
  (changeImpacts) => changeImpacts.retrieveChangeImpact
);

export const selectSearchChangeImpactsState = (state) =>
  state.changeImpacts.searchChangeImpacts;

export const selectRetrieveImpactedEntitiesState = (state) =>
  state.changeImpacts.retrieveImpactedEntities;

export const selectChangeImpact = (id) =>
  createSelector(selectEntities, (entities) =>
    denormalize(id, changeImpactSchema, entities)
  );
export const selectImpactedEntities = (id) => (state) =>
  denormalize(id, impactedEntitiesSchema, state.entities);

export const selectChangeImpactParticipants = (id) => (state) =>
  denormalize(id, changeImpactParticipantsSchema, state.entities);

const selectSearchChangeImpactIds = createSelector(
  selectSearchChangeImpactsState,
  (state) => state.changeImpactIds
);

export const selectSearchResults = createSelector(
  selectSearchChangeImpactIds,
  selectEntities,
  (changeImpactIds, entities) =>
    denormalize(changeImpactIds, [changeImpactSchema], entities)
);
